import { Routes } from '@angular/router';
import { VendorOverviewRedirectGuard } from 'app/shared/guards/vendor-overview-redirect-guard';
import { PayorTypeResolver } from 'app/shared/lookup/property-lookup/payor-type.resolver';
import { AppMenuComponent } from '../menus/app-menu/app-menu.component';
import { EventMenuComponent } from '../menus/event-menu/event-menu.component';
import { CanActivateViaAuthGuard } from '../shared/guards/can-activate-via-auth-guard';
import { DashboardRedirectGuard } from '../shared/guards/dashboard-redirect-guard';
import { FinalPaymentAuthorizedGuard } from '../shared/guards/final-payment-authorized.guard';
import { OverviewRedirectGuard } from '../shared/guards/overview-redirect-guard';
import { PermissionGuard } from '../shared/guards/permission-guard';
import { AddEventComponent } from './add-event/add-event.component';
import { AttachmentResolver } from './attachment/attachment.resolver';
import { DashboardComponent } from './dashboard/dashboard-page/dashboard.component';
import { EventListComponent } from './event-list/event-list.component';
import { EventMatchComponent } from './event-match/event-match.component';
import { EventComponent } from './event/event.component';
import { EventResolver } from './event/event.resolver';
import { MatchingPropertyCandidateComponent } from './matching-property/matching-property-candicate/matching-property-candidate.component';
import { MatchingPropertyComponent } from './matching-property/matching-property.component';
import { OverviewPageComponent } from './overview/overview-page/overview-page.component';
import { OverviewResolver } from './overview/overview.resolver';
import { TransactionResolver } from './overview/transaction.resolver';
import { VendorOverviewComponent } from './overview/vendor-overview-page/vendor-overview-page.component';
import { VendorTransactionResolver } from './overview/vendor-transaction.resolver';
import { AdjustmentTypeCreditResolver } from './payment/adjustment-type-credit.resolver';
import { AdjustmentTypeDebitResolver } from './payment/adjustment-type-debit.resolver';
import { CommissionComponent } from './payment/commission/commission.component';
import { FinalPaymentStatusResolver } from './payment/final-payment-status.resolver';
import { LatestPaymentResolver } from './payment/latest-payment.resolver';
import { PaymentsResolver } from './payment/payments.resolver';
import { LatestPrepaymentResolver } from './payment/prepayment.resolver';
import { TaxDeterminationResolver } from './payment/tax-determination.resolver';
import { VendorCommissionComponent } from './payment/vendor-commission/vendor-commission.component';
import { VendorsTotalPaymentsResolver } from './payment/vendorsTotalPayments.resolver';
import { CanDeactiveReservationDetailTable } from './reservations/reservation-detail-table/reservation-detail-can-deactive';
import { ReservationDetailResolver } from './reservations/reservation-detail.resolver';
import { ReservationPageComponent } from './reservations/reservation-page/reservation-page.component';
import { ReservationCompareResolver } from './reservations/reservation.compare.resolver';
import { ReservationResolver } from './reservations/reservation.resolver';
import { TransientReservationComponent } from './reservations/transient-reservation/transient-reservation.component';
import { RsvpCompareResolver } from './rsvp/rsvp-compare.resolver';
import { RsvpDetailResolver } from './rsvp/rsvp-detail.resolver';
import { RsvpComponent } from './rsvp/rsvp-page/rsvp-page.component';
import { RsvpResolver } from './rsvp/rsvp.resolver';
import { VendorRsvpComponent } from './rsvp/vendor-rsvp-page/vendor-rsvp-page.component';
import { ChainResolver } from './service/chainbrand/chain.resolver';
import { CountryResolver } from './service/common-data/country.resolver';
import { CurrencyResolver } from './service/common-data/currency.resolver';
import { VendorReservationComponent } from './reservations/vendor-reservation-page/vendor-reservation-page.component';
import { OoivRulesValidationResolver } from './payment/ooiv-rules-validation.resolver';
import { UnsavedChangesGuard } from 'app/shared/guards/unsaved-changes/unsaved-changes.guard';
import { CustomLabelResolver } from './basic-event-data/custom-label.resolver';
import { InitialPaymentResolver } from './payment/initial-payment.resolver';
import { EventListResolver } from './service/event-list.resolver';
import { AgencyDashboardComponent } from './agency-dashboard/agency-dashboard.component';
import { InvoiceHistoryComponent } from './invoice-history/invoice-history.component';
import { InvoiceHistoryVendorComponent } from './invoice-history-vendor/invoice-history-vendor.component';
import { InvoiceHistoryUserComponent } from './invoice-history-user/invoice-history-user.component';
import { InvoiceHistoryResolver } from './service/invoice-history.resolver';
import { EventHasItemsResolver } from './service/event-has-items.resolver';
import { MatchingAgencyComponent } from './matching-agency/matching-agency.component';
import { MatchingAgencyCandidateComponent } from './matching-agency/matching-agency-candidate/matching-agency-candidate.component';
import { AgencyDashboardRedirectGuard } from '../shared/guards/agency-dashboard-redirect-guard';
import { PaymentSummaryResolver } from './service/payment-summary/payment-summary.resolver';

export const routes: Routes = [
  {
    path: '',
    redirectTo: '/dashboard',
    pathMatch: 'full',
    resolve: {
      invoiceHistoryData: InvoiceHistoryResolver
    }
  },
  {
    path: 'dashboard',
    canActivate: [ DashboardRedirectGuard, CanActivateViaAuthGuard ],
    children: [
      {
        path: '',
        outlet: 'navigation',
        component: AppMenuComponent
      },
      {
        path: '',
        component: DashboardComponent
      }
    ]
  },
  {
    path: 'dashboard-agency',
    canActivate: [ AgencyDashboardRedirectGuard, CanActivateViaAuthGuard ],
    children: [
      {
        path: '',
        outlet: 'navigation',
        component: AppMenuComponent
      },
      {
        path: '',
        component: AgencyDashboardComponent
      }
    ]
  },
  {
    path: 'events',
    canActivate: [ CanActivateViaAuthGuard ],
    children: [
      {
        path: '',
        outlet: 'navigation',
        component: AppMenuComponent
      },
      {
        path: '',
        component: EventListComponent,
        resolve: {
          countriesData: CountryResolver,
          eventListData: EventListResolver
        },
        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
        data: { view: 'default' }
      },
      {
        path: 'financial',
        component: EventListComponent,
        resolve: {
          eventListData: EventListResolver
        },
        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
        data: { view: 'financial' }
      },
      {
        path: 'add',
        component: AddEventComponent,
        canActivate: [ PermissionGuard ],
        canDeactivate: [ UnsavedChangesGuard ],
        data: { allowedPermissions: [ 'addevent' ] },
        resolve: {
          countriesData: CountryResolver,
          currenciesData: CurrencyResolver,
          chainsData: ChainResolver,
          payorTypesData: PayorTypeResolver,
          customLabelData: CustomLabelResolver
        }
      },
      {
        path: 'matching/property',
        component: MatchingPropertyComponent,
        canActivate: [ PermissionGuard ],
        data: { allowedPermissions: [ 'viewInternalReview' ] },
        resolve: {
        }
      },
      {
        path: 'matching/property/candidates',
        component: MatchingPropertyCandidateComponent,
        canActivate: [ PermissionGuard ],
        data: { allowedPermissions: [ 'viewInternalReview' ] },
        resolve: {
        }
      },
      {
        path: 'matching/agency',
        component: MatchingAgencyComponent,
        canActivate: [ PermissionGuard ],
        data: { allowedPermissions: [ 'viewInternalReview' ] },
        resolve: {
        }
      },
      {
        path: 'matching/agency/candidates',
        component: MatchingAgencyCandidateComponent,
        canActivate: [ PermissionGuard ],
        data: { allowedPermissions: [ 'viewInternalReview' ] },
        resolve: {
        }
      },
      {
        path: 'event-match',
        component: EventMatchComponent,
        /* resolve: {
          eventsData: EventsResolver
        }, */
        /* runGuardsAndResolvers: 'paramsOrQueryParamsChange', */
        /* data: { view: 'financial' } */
      },
      {
        path: 'invoice-history',
        component: InvoiceHistoryUserComponent
      }
    ]
  },
  { /* Multi Vendor users */
    path: 'event/:id',
    canActivate: [ CanActivateViaAuthGuard ],
    resolve: {
      eventsData: EventResolver,
      countriesData: CountryResolver,
      currenciesData: CurrencyResolver,
      latestPaymentData: LatestPaymentResolver,
      finalPaymentStatusData: FinalPaymentStatusResolver,
      payorTypesData: PayorTypeResolver,
      customLabelData: CustomLabelResolver
    },
    children: [
      {
        path: '',
        outlet: 'navigation',
        component: AppMenuComponent,
        children: [
          {
            path: '',
            component: EventMenuComponent,
            outlet: 'event'
          }
        ]
      },
      {
        path: '',
        redirectTo: 'overview',
        pathMatch: 'full'
      },
      {
        path: 'overview',
        component: VendorOverviewComponent,
        canActivate: [ VendorOverviewRedirectGuard ],
        resolve: {
          overviewData: OverviewResolver,
          transactions: VendorTransactionResolver,
          reservationData: ReservationResolver,
          rsvpData: RsvpResolver
        }
      },
      {
        path: 'summary',
        component: EventComponent,
        resolve: {
          attachmentData: AttachmentResolver,
          paymentsData: PaymentsResolver,
          eventHasItemsData: EventHasItemsResolver
        }
      },
      {
        path: 'summary/edit',
        component: EventComponent,
        resolve: {
          attachmentData: AttachmentResolver,
          paymentsData: PaymentsResolver,
          latestPrepaymentData: LatestPrepaymentResolver,
          latestPaymentData: LatestPaymentResolver,
          chainsData: ChainResolver,
          eventHasItemsData: EventHasItemsResolver
        },
        canActivate: [ PermissionGuard, FinalPaymentAuthorizedGuard ],
        canDeactivate: [ UnsavedChangesGuard ],
        data: { allowedPermissions: [ 'editevent' ] }
      },
      {
        path: 'commission',
        component: VendorCommissionComponent,
        resolve: {
          taxDeterminationData: TaxDeterminationResolver,
          vendorsTotalPaymentsData: VendorsTotalPaymentsResolver,
          paymentsData: PaymentsResolver,
          latestPrepaymentData: LatestPrepaymentResolver,
          adjustmentTypeCreditData: AdjustmentTypeCreditResolver,
          adjustmentTypeDebitData: AdjustmentTypeDebitResolver,
          ooivRulesValidation: OoivRulesValidationResolver,
          initialPaymentData: InitialPaymentResolver,
          PaymentSummaryResolver
        },
        canActivate: [ PermissionGuard ],
        data: { allowedPermissions: [ 'multivendor' ] }
      },
      {
        path: 'invoice-history',
        component: InvoiceHistoryVendorComponent,
      },
      {
        path: 'rsvp',
        component: VendorRsvpComponent,
        resolve: {
          rsvpData: RsvpCompareResolver
        }
      },
      {
        path: 'rsvp/detail',
        component: VendorRsvpComponent,
        resolve: {
          rsvpDetail: RsvpDetailResolver
        },
        canDeactivate: [CanDeactiveReservationDetailTable]
      },
      {
        path: 'reservations',
        component: VendorReservationComponent,
        resolve: {
          reservations: ReservationCompareResolver,
          reservationData: ReservationResolver
        }
      },
      {
        path: 'reservations/detail',
        component: VendorReservationComponent,
        resolve: {
          reservationsDetail: ReservationDetailResolver
        },
        canDeactivate: [CanDeactiveReservationDetailTable]
      },
      {
        path: 'transient-reservations',
        component: TransientReservationComponent,
        canActivate: [ PermissionGuard ],
        data: { allowedPermissions: [ 'linkreservation' ] }
      }
    ]
  },
  { /* Hotel users as children */
    path: 'event/:id/:eventPartyId',
    canActivate: [ CanActivateViaAuthGuard ],
    resolve: {
      eventsData: EventResolver,
      countriesData: CountryResolver,
      currenciesData: CurrencyResolver,
      latestPaymentData: LatestPaymentResolver,
      finalPaymentStatusData: FinalPaymentStatusResolver,
      payorTypesData: PayorTypeResolver,
      customLabelData: CustomLabelResolver,
      initialPaymentData: InitialPaymentResolver
    },
    children: [
      {
        path: '',
        outlet: 'navigation',
        component: AppMenuComponent,
        children: [
          {
            path: '',
            component: EventMenuComponent,
            outlet: 'event'
          }
        ]
      },
      {
        path: '',
        redirectTo: 'overview',
        pathMatch: 'full'
      },
      {
        path: 'overview',
        component: OverviewPageComponent,
        canActivate: [ OverviewRedirectGuard ],
        resolve: {
          overviewData: OverviewResolver,
          transactions: TransactionResolver,
          reservationData: ReservationResolver,
          rsvpData: RsvpResolver
        }
      },
      {
        path: 'summary',
        component: EventComponent,
        resolve: {
          attachmentData: AttachmentResolver,
          paymentsData: PaymentsResolver,
          eventHasItemsData: EventHasItemsResolver
        },
        data: { allPayments: true }
      },
      {
        path: 'summary/edit',
        component: EventComponent,
        resolve: {
          attachmentData: AttachmentResolver,
          paymentsData: PaymentsResolver,
          latestPrepaymentData: LatestPrepaymentResolver,
          chainsData: ChainResolver,
          eventHasItemsData: EventHasItemsResolver
        },
        canActivate: [ PermissionGuard, FinalPaymentAuthorizedGuard ],
        canDeactivate: [ UnsavedChangesGuard ],
        data: { allowedPermissions: [ 'editevent' ] }
      },
      {
        path: 'commission',
        component: CommissionComponent,
        resolve: {
          paymentsData: PaymentsResolver,
          latestPrepaymentData: LatestPrepaymentResolver,
          adjustmentTypeCreditData: AdjustmentTypeCreditResolver,
          adjustmentTypeDebitData: AdjustmentTypeDebitResolver,
          taxDeterminationData: TaxDeterminationResolver,
          ooivRulesValidation: OoivRulesValidationResolver,
          initialPaymentData: InitialPaymentResolver,
          PaymentSummaryResolver
        },
        canActivate: [ PermissionGuard ],
        data: { allowedPermissions: [ 'singlevendor' ] }
      },
      {
        path: 'invoice-history',
        component: InvoiceHistoryComponent,
      },
      {
        path: 'rsvp',
        component: RsvpComponent,
        resolve: {
          rsvpData: RsvpCompareResolver
        }
      },
      {
        path: 'rsvp/detail',
        component: RsvpComponent,
        resolve: {
          rsvpDetail: RsvpDetailResolver
        },
        canDeactivate: [CanDeactiveReservationDetailTable]
      },
      {
        path: 'reservations',
        component: ReservationPageComponent,
        resolve: {
          reservations: ReservationCompareResolver,
          reservationData: ReservationResolver
        }
      },
      {
        path: 'reservations/detail',
        component: ReservationPageComponent,
        resolve: {
          reservationsDetail: ReservationDetailResolver
        },
        canDeactivate: [CanDeactiveReservationDetailTable]
      },
      {
        path: 'transient-reservations',
        component: TransientReservationComponent,
        canActivate: [ PermissionGuard ],
        data: { allowedPermissions: [ 'linkreservation' ] }
      }
    ]
  }
];



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/event/event.routes.ts